import { useEffect, useRef } from "react";
import Link from "next/link";

import {
  HALLOWEEN_2026_COPY_READY,
  HALLOWEEN_2026_STRIP_CTA_HREF,
} from "@/constants/halloween-2026";
import { mountStrip2026Deco } from "@/libs/halloween-2026/strip-deco";
import { useTrans } from "@/utils/useTrans";
import { usePromotionTracking } from "@/libs/analytics/use-promotion-tracking";
import {
  StripSceneLeft,
  StripSceneRight,
} from "@/v2-components/event/halloween-2026-strip-scenes";

/** 구분선 — 글자색 45% */
const Sep = () => (
  <span aria-hidden className="text-[#FF9D57]/45">
    |
  </span>
);

/**
 * 할로윈 2026 띠배너 (#6061 H5) — GNB 아래 띠(`PromoBanner` 자리)를 기간 중 이것으로 바꾼다.
 *
 * Figma `Halloween 2026 — Strip Banner` (`31437:7443`): 데스크톱 `31437:7483` · 모바일 `31437:7506`.
 *
 * ```
 *   높이     40 (모바일 44) · 배경 Purple/100 #1C0F30 · 글자 Orange Hover/30 #FF9D57 · CTA Yellow/20 #FDDC69
 *   데스크톱  이름 | 메시지 | 기간 | CTA →   (14px · 이름 · CTA 600 · 나머지 500 · 간격 12 · 구분선 45%)
 *   모바일    이름 | 기간
 *   장식      GNB 컨테이너와 같은 폭(max 1440 · 좌우 32 / 모바일 16) · 문구 뒤
 *            데스크톱 = 좌우 장면 + 별 · 모바일 = 별만 · 가운데는 받침(1000×72 방사형)으로 비움
 * ```
 *
 * ⚠️ **문구 · CTA 는 기획 전달 대기**(`HALLOWEEN_2026_COPY_READY`). 그 전엔 데스크톱도 모바일과 같은 「이름 | 기간」만
 * 그리고 링크가 아니다 — 빈 칸이나 「TBD」를 내보내지 않는다.
 *
 * 노출 여부는 여기서 판정하지 않는다 — `PromoBanner` 가 `useHalloween2026()` 으로 정한다.
 */
export function Halloween2026Strip() {
  const { t } = useTrans(["gnb"]);
  const decoRef = useRef<HTMLDivElement>(null);
  // 노출·클릭 측정(D1) — 문구 준비 전(링크 없는 판)이어도 노출은 센다.
  const { attach: attachPromo, onClickCapture: onPromoClick } =
    usePromotionTracking({
      id: "halloween-2026",
      slot: "top_strip",
    });

  useEffect(() => {
    if (!decoRef.current) return;
    return mountStrip2026Deco(decoRef.current);
  }, []);

  const name = t("halloween2026Name");
  const period = t("halloween2026Period");
  const withCopy =
    HALLOWEEN_2026_COPY_READY && HALLOWEEN_2026_STRIP_CTA_HREF !== null;

  const content = (
    <>
      <div
        ref={decoRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
      >
        {/* GNB 컨테이너와 같은 폭(max 1440). 시안 기준점이 다르다 —
            별: 데스크톱은 컨테이너 전체(1440) · 모바일은 패딩 안(16 들임)
            장면: 패딩 32 + 들임 min(8%, 120px)(1440 에서 115 → 147)
            받침: 1000×72 타원 · 문구 가운데 · 데스크톱만 */}
        <div className="relative mx-auto h-full max-w-[1440px]">
          <div
            data-hw26-sky=""
            className="hw26-strip-sky hw26-mask absolute inset-x-4 inset-y-0 opacity-[0.85] laptop:inset-x-0"
          />
          <div
            className="absolute left-1/2 top-[-16px] hidden h-[72px] w-[min(1000px,70%)] -translate-x-1/2 laptop:block"
            style={{
              background:
                "radial-gradient(closest-side, rgba(28,15,48,0.95), rgba(28,15,48,0.7) 60%, rgba(28,15,48,0))",
            }}
          />
          <StripSceneLeft className="absolute bottom-0 left-[calc(32px+min(8%,120px))] hidden h-full w-auto laptop:block" />
          <StripSceneRight className="absolute bottom-0 right-[calc(32px+min(8%,120px))] hidden h-full w-auto laptop:block" />
        </div>
      </div>

      <div className="relative z-[1] flex h-full items-center justify-center gap-3 whitespace-nowrap px-4 text-base font-500 text-[#FF9D57]">
        <span className="font-600">{name}</span>
        {withCopy && (
          <span className="hidden laptop:contents">
            <Sep />
            <span>{t("halloween2026Message")}</span>
          </span>
        )}
        <Sep />
        <span>{period}</span>
        {withCopy && (
          <span className="hidden laptop:contents">
            <Sep />
            <span className="font-600 text-[#FDDC69]">
              {t("halloween2026Cta")} →
            </span>
          </span>
        )}
      </div>
    </>
  );

  const className =
    "relative isolate block h-11 overflow-hidden bg-[#1C0F30] laptop:h-10";

  if (withCopy && HALLOWEEN_2026_STRIP_CTA_HREF) {
    return (
      <Link
        href={HALLOWEEN_2026_STRIP_CTA_HREF}
        className={className}
        data-hw26-strip=""
        ref={attachPromo}
        onClickCapture={onPromoClick}
      >
        {content}
      </Link>
    );
  }
  return (
    <div className={className} data-hw26-strip="" ref={attachPromo}>
      {content}
    </div>
  );
}
